import React, { useEffect, useState } from "react";
import { Plus, Heart, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useUserAuth } from "../context/UserAuthContext";
import { userApi } from "../lib/api";
import { productPath } from "../lib/productSlug";

export const ProductCard = ({ product, wishlistIds = [], onWishlistChange = () => {} }) => {
  const { addItem } = useCart();
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const isWishlisted = wishlistIds.includes(product.id);

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error(t("product.login_to_wishlist"));
      navigate("/login");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      if (isWishlisted) {
        await userApi.delete(`/users/me/wishlist/${product.id}`);
        toast.success(t("product.wishlist_removed"));
      } else {
        await userApi.post(`/users/me/wishlist/${product.id}`);
        toast.success(t("product.wishlist_added"));
      }
      onWishlistChange();
    } catch (err) {
      toast.error("Error");
    } finally { setBusy(false); }
  };

  return (
    <article data-testid={`product-card-${product.id}`}
      className="group relative bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 transition-all duration-300">
      {/* Image */}
      <Link to={productPath(product)} className="relative aspect-square overflow-hidden bg-zinc-950 block">
        {/* Back-light: yellow radial spotlight behind the product, fades in on hover */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-0"
          style={{
            background:
              "radial-gradient(circle at 50% 55%, rgba(250,204,21,0.55) 0%, rgba(250,204,21,0.25) 25%, rgba(250,204,21,0) 65%)",
            mixBlendMode: "screen",
          }}
        />
        <img src={product.image} alt={product.title}
          className="relative z-10 w-full h-full object-contain p-2 group-hover:scale-110 transition-transform duration-700 group-hover:drop-shadow-[0_0_22px_rgba(250,204,21,0.45)]" />
        <div className="absolute inset-0 halftone-bg opacity-30 mix-blend-overlay pointer-events-none z-20" />

        {/* Ambient rim light at the bottom on hover */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-1/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-20"
          style={{
            background:
              "linear-gradient(to top, rgba(250,204,21,0.18), transparent 80%)",
            mixBlendMode: "screen",
          }}
        />

        {product.limited && (
          <div className="absolute top-3 left-3 bg-yellow-400 text-black px-3 py-1 font-heading text-xs uppercase tracking-widest brutal-shadow-black">
            {t("product.limited")}
          </div>
        )}
        {product.bestseller && !product.limited && (
          <div className="absolute top-3 left-3 bg-black border-2 border-yellow-400 text-yellow-400 px-3 py-1 font-heading text-xs uppercase tracking-widest">
            {t("product.bestseller")}
          </div>
        )}
        {product.stock <= 3 && product.stock > 0 && (
          <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 font-heading text-xs uppercase">
            {t("product.only_x_left", { n: product.stock })}
          </div>
        )}

        <button data-testid={`wishlist-${product.id}`} onClick={toggleWishlist}
          className={`absolute top-3 ${product.stock <= 3 && product.stock > 0 ? "right-20" : "right-3"} p-2 border-2 transition ${
            isWishlisted ? "bg-yellow-400 text-black border-yellow-400" : "bg-black/60 border-zinc-700 text-white hover:border-yellow-400 hover:text-yellow-400"
          }`} aria-label="wishlist">
          <Heart className="w-4 h-4" strokeWidth={3} fill={isWishlisted ? "currentColor" : "none"} />
        </button>

        <div className="absolute bottom-0 left-0 right-0 bg-yellow-400 text-black py-3 font-heading uppercase tracking-widest text-sm translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex items-center justify-center gap-2">
          <Eye className="w-4 h-4" strokeWidth={3} /> {t("product.view_details")}
        </div>
      </Link>

      <div className="p-5 border-t-2 border-zinc-800">
        <div className="flex items-center justify-between mb-2">
          <span className="font-jp text-yellow-400 text-[10px] tracking-widest">{product.category.toUpperCase()}</span>
          <span className="text-xs text-zinc-500 uppercase tracking-widest">#{product.id}</span>
        </div>
        <Link to={productPath(product)} className="block">
          <h3 className="font-heading text-xl sm:text-2xl uppercase text-white leading-tight hover:text-yellow-400 transition">
            {product.title}
          </h3>
        </Link>
        <div className="flex items-end justify-between mt-3">
          <div className="font-heading text-2xl text-yellow-400">€ {product.price.toFixed(2)}</div>
          <button data-testid={`add-to-cart-${product.id}`} onClick={() => addItem(product)}
            className="bg-zinc-800 text-yellow-400 p-2 hover:bg-yellow-400 hover:text-black transition border-2 border-zinc-800 hover:border-yellow-400"
            aria-label="add">
            <Plus className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      </div>
    </article>
  );
};
